/**
 * Temporary screen-scoped design tokens for the **Sign Request Editor** step of
 * `/create/request?type=others`.
 *
 * Mirrors `Sign Request Editor.dc.html` 1:1 — every colour, size, radius,
 * spacing and type value the screen needs lives here so the components never
 * inline a literal. Once every screen is re-skinned these get promoted into
 * `src/theme` and this file is deleted (see UI_IMPLEMENTATION_RULES.md
 * § Temporary Screen Tokens).
 */

import { inkfree, mobile, notification, signer, white } from "theme/colors";

/** The raw palette the design draws from. */
const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  label: signer.text.meta,
  muted: signer.text.faint,
  faint: mobile.base[100],
  borderLight: signer.border.light,
  borderSoft: signer.border.main,
  borderMain: signer.disabled,
  borderStrong: signer.border.strong,
  surface: signer.surface.muted,
  surfaceHover: signer.surface.subtle,
  white,
  accent: signer.orange.main,
  accentHover: signer.orange.dark,
  /**
   * Solid CTA fills (primary "Next" button + caret, active toggle) — per
   * the standing brand-colour rule these are the app brand red, not
   * `accent`/`accentHover` above, which stay orange for text/borders/dots.
   */
  ctaBg: inkfree.main,
  ctaBgHover: inkfree.dark,
  accentTint: signer.orange.tint,
  accentBorder: signer.orange.border,
  accentSoft: signer.orange.faintest,
  accentDisabled: "#F0AE95",
  amber: "#F5B21E",
  green: "#3AA76D",
  danger: "#E8474B",
  dangerTint: notification.error.bg,
} as const;

export const signRequestEditorTokens = {
  palette,

  /** Top toolbar shared by every step of the create flow. */
  header: {
    height: 58,
    paddingX: "18px",
    gap: "18px",
    bg: palette.white,
    borderColor: palette.borderLight,
    closeSize: 34,
    closeRadius: "9px",
    closeIconSize: 18,
    closeColor: palette.body,
    hoverBg: palette.surface,
    savedGap: "6px",
    savedFontSize: "12.5px",
    savedColor: palette.muted,
    savedIconSize: 14,
    savedIconColor: palette.green,
    dividerWidth: "1px",
    dividerHeight: 26,
    buttonHeight: 38,
    buttonRadius: "9px",
    buttonFontSize: "13.5px",
    buttonFontWeight: 700,
    previousPaddingX: "18px",
    previousBorderWidth: "1.5px",
    nextPaddingX: "22px",
    nextCaretWidth: 34,
    nextCaretIconSize: 14,
    nextCaretDivider: "rgba(255,255,255,.35)",
  },

  /** Dot › label › chevron breadcrumb in the header. */
  stepper: {
    gap: "6px",
    dotSize: 9,
    dotDone: palette.accent,
    dotTodo: palette.borderMain,
    fontSize: "13.5px",
    weightCurrent: 700,
    weightOther: 600,
    colorCurrent: palette.accent,
    colorDone: palette.ink,
    colorTodo: palette.muted,
    arrowSize: 14,
    arrowColor: palette.borderStrong,
    arrowMarginX: "4px",
  },

  /** Left "place a field" rail. */
  leftRail: {
    width: 272,
    borderColor: palette.borderLight,
    headerPadding: "14px",
    /** PLACING FIELDS FOR / FREQUENTLY USED / FIELDS / PERSONAL INFO. */
    eyebrowFontSize: "11px",
    eyebrowLetterSpacing: ".12em",
    eyebrowFontWeight: 700,
    eyebrowColor: palette.muted,
    eyebrowMarginBottom: "8px",
    groupLabelMarginBottom: "9px",
    signerPadding: "9px 11px",
    signerRadius: "9px",
    signerBorder: palette.borderMain,
    signerGap: "10px",
    signerAvatarSize: 26,
    signerAvatarFontSize: "11px",
    signerAvatarFontWeight: 700,
    signerNameFontSize: "13.5px",
    signerNameFontWeight: 600,
    signerRoleFontSize: "11px",
    signerCaretSize: 15,
    swatchRowGap: "6px",
    swatchRowMarginTop: "9px",
    swatchSize: 18,
    swatchRingWidth: "2px",
    manageGap: "5px",
    manageFontSize: "12px",
    manageFontWeight: 700,
    manageIconSize: 13,
    managePadding: "3px 6px",
    manageRadius: "7px",
    searchPadding: "12px 14px 0",
    /** 8px + 16px (13px Montserrat at `normal`) + 8px + 2px border. */
    searchHeight: 34,
    searchBoxPadding: "8px 11px",
    searchRadius: "9px",
    searchGap: "8px",
    searchIconSize: 15,
    searchFontSize: "13px",
    listPadding: "14px",
    /**
     * The 9px scrollbar is taken out of the content box, so the design's flat
     * 14px inset leaves the right gutter 9px wider than the left *and* squeezes
     * the two-up tiles. Pairing a 5px inset with the scrollbar restores both:
     * 5 + 9 reads as the design's 14px gutter, and the tiles get their intended
     * width back.
     */
    listPaddingRight: "5px",
    sectionMarginBottom: "18px",
    /** Two-up "Frequently used" tiles. */
    quickGap: "8px",
    /**
     * Measured: "Date signed" needs 77.9px at 12.5px/600 Montserrat. The
     * design's `9px 10px` + `gap:8px` only offers 67px, so the label wrapped
     * onto a second line. This trim offers 80.5px.
     */
    quickPadding: "9px 7px",
    quickInnerGap: "5px",
    quickRadius: "9px",
    quickIconSize: 16,
    quickFontSize: "12.5px",
    quickFontWeight: 600,
    /** Full-width field rows. */
    rowGap: "7px",
    rowPadding: "10px 12px",
    rowRadius: "9px",
    rowInnerGap: "11px",
    rowIconSize: 18,
    rowFontSize: "13px",
    rowFontWeight: 600,
    rowBorder: palette.borderLight,
    rowHoverBorder: palette.accentBorder,
    rowHoverShadow: "0 1px 3px rgba(33,21,71,.08)",
    handleIconSize: 14,
    handleColor: palette.muted,
  },

  /** Document canvas between the two rails. */
  canvas: {
    bg: palette.surface,
    // Vertical 28px kept; horizontal trimmed to 12px so the document viewer has
    // less empty space on the left/right and the page gets more room.
    padding: "28px 12px",
    toolbarHeight: 46,
    toolbarPaddingX: "14px",
    toolbarGap: "8px",
    toolbarBorder: palette.borderLight,
    toggleSize: 32,
    toggleRadius: "7px",
    toggleIconSize: 17,
    toggleColor: palette.body,
    fileNameFontSize: "13px",
    fileNameFontWeight: 600,
    pageMetaFontSize: "12px",
    pageMetaColor: palette.muted,
    zoomRadius: "9px",
    zoomPadding: "3px",
    zoomGap: "2px",
    zoomButtonSize: 28,
    zoomButtonRadius: "7px",
    zoomIconSize: 15,
    zoomValueFontSize: "12.5px",
    zoomValueFontWeight: 600,
    zoomValueMinWidth: 42,
    actionSize: 32,
    actionRadius: "7px",
    actionIconSize: 16,
    dividerHeight: 24,
    /** The rendered PDF page. */
    pageBorder: palette.borderLight,
    pageShadow: "0 10px 30px rgba(33,21,71,.08)",
    pageGap: "24px",
  },

  /** "Inkfree AI found N likely signature spots" pill. */
  aiBanner: {
    top: "14px",
    bg: palette.ink,
    color: palette.white,
    radius: "999px",
    padding: "8px 8px 8px 16px",
    gap: "10px",
    shadow: "0 10px 30px rgba(33,21,71,.2)",
    iconSize: 16,
    iconColor: palette.amber,
    fontSize: "12.5px",
    fontWeight: 500,
    actionBg: "rgba(255,255,255,.16)",
    actionHoverBg: "rgba(255,255,255,.26)",
    actionFontSize: "12px",
    actionFontWeight: 600,
    actionPadding: "5px 12px",
    actionRadius: "999px",
    closeIconSize: 14,
  },

  /** Floating "‹ 1 / 3 ›" pill at the bottom of the canvas. */
  pageNav: {
    bottom: "18px",
    bg: palette.white,
    border: palette.borderLight,
    radius: "999px",
    padding: "5px",
    gap: "4px",
    shadow: "0 10px 30px rgba(33,21,71,.12)",
    buttonSize: 30,
    iconSize: 15,
    idleColor: palette.muted,
    activeColor: palette.ink,
    labelFontSize: "12.5px",
    labelFontWeight: 600,
    labelPaddingX: "6px",
  },

  /** Placed field chip drawn on the page. */
  placedField: {
    borderWidth: "1.5px",
    radius: "6px",
    padding: "0 10px",
    gap: "6px",
    iconSize: 14,
    fontSize: "12px",
    fontWeight: 600,
    hoverShadow: "0 10px 30px rgba(33,21,71,.12)",
    /** 8% fill, expressed as the hex-alpha suffix appended to `#RRGGBB`. */
    tintHex: "14",
  },

  /** Right-hand panel (Documents / Properties / Assistant / Insights). */
  rightRail: {
    // Narrowed from 344 so the centered document viewer gets more room; the
    // panel + far-right icon strip width all derive from this one token, and
    // mobile ignores it (the panel goes full-width there).
    width: 280,
    borderColor: palette.borderLight,
    headerHeight: 46,
    headerPaddingX: "16px",
    headerGap: "9px",
    headerIconSize: 18,
    headerFontSize: "14px",
    headerFontWeight: 700,
    collapseSize: 28,
    collapseRadius: "7px",
    collapseIconSize: 16,
    bodyPadding: "16px 20px",
    footerPadding: "12px 16px",
    footerGap: "10px",
  },

  /** Icon strip pinned to the far right edge. */
  tabRail: {
    width: 56,
    borderColor: palette.borderLight,
    paddingY: "12px",
    gap: "6px",
    buttonSize: 40,
    buttonRadius: "9px",
    iconSize: 19,
    activeBg: palette.ctaBg,
    activeColor: palette.white,
    idleColor: palette.body,
    hoverBg: palette.surface,
    lockBadgeSize: 14,
    lockIconSize: 10,
  },

  /** AI Assistant panel. */
  assistant: {
    padding: "20px",
    gap: "10px",
    introFontSize: "12px",
    introColor: palette.label,
    introLineHeight: 1.6,
    introMarginBottom: "4px",
    promptPadding: "11px 14px",
    promptRadius: "10px",
    promptFontSize: "12.5px",
    promptFontWeight: 600,
    promptColor: palette.body,
    promptLineHeight: 1.5,
    promptHoverBorder: palette.accentBorder,
    promptHoverBg: palette.accentTint,
    pickerHeight: 34,
    pickerPaddingX: "10px",
    pickerRadius: "8px",
    pickerBorderWidth: "1.5px",
    pickerFontSize: "11.5px",
    pickerFontWeight: 700,
    pickerGap: "8px",
    pickerIconSize: 14,
    pickerCaretSize: 11,
    composerMinHeight: 42,
    composerPadding: "5px 6px 5px 14px",
    composerRadius: "10px",
    composerBorderWidth: "1.5px",
    composerFontSize: "12.5px",
    sendSize: 32,
    sendRadius: "8px",
    sendIconSize: 14,
    disclaimerFontSize: "10px",
    disclaimerColor: palette.muted,
    disclaimerLineHeight: 1.5,
    bubblePadding: "10px 13px",
    bubbleFontSize: "12.5px",
    bubbleLineHeight: 1.6,
    bubbleUserRadius: "12px 12px 3px 12px",
    bubbleAiRadius: "12px 12px 12px 3px",
    bubbleMaxWidth: "88%",
    bubbleGap: "12px",
    scopeFontSize: "10px",
    scopeFontWeight: 700,
  },

  /** Documents panel. */
  documents: {
    padding: "16px 20px",
    metaFontSize: "11.5px",
    metaFontWeight: 700,
    metaMarginBottom: "12px",
    addFontSize: "12px",
    addFontWeight: 700,
    addPadding: "5px 11px",
    addRadius: "999px",
    cardRadius: "12px",
    cardPadding: "13px 15px",
    cardGap: "10px",
    cardMarginBottom: "10px",
    cardIconSize: 16,
    cardNameFontSize: "12.5px",
    cardNameFontWeight: 700,
    cardMetaFontSize: "10.5px",
    chipGap: "8px",
    chipPadding: "3px 8px 3px 4px",
    chipRadius: "999px",
    chipFontSize: "10.5px",
    chipFontWeight: 700,
    chipAvatarSize: 15,
    chipAvatarFontSize: "8px",
    countFontSize: "11px",
    countFontWeight: 700,
    hintFontSize: "11.5px",
    hintLineHeight: 1.6,
    hintMarginTop: "14px",
  },

  /** 9px scrollbars used throughout the editor chrome. */
  scrollbar: {
    size: "9px",
    thumb: palette.borderMain,
    thumbBorder: "2px solid transparent",
    radius: "99px",
  },
} as const;

export default signRequestEditorTokens;
